import type { CatalogueProduct } from '@/api/services/billingPurchases';
import { PACKAGE_KEYS, type PackageKey, type ProductKey } from '@/api/services/billingProducts';

/**
 * What a `/buy/...` link asks for: a product and, for some products, a
 * quantity. Nothing in the URL is a price: the page prices the request from
 * the catalogue, and checkout is asked only for a product and a quantity.
 *
 * - `/buy/seats?quantity=N` and `/buy/hours?quantity=N`
 * - `/buy/topup?amount=E`, E in euros with at most two decimals
 * - `/buy/package/<silver|gold|platinum>`, always one
 *
 * A missing quantity or amount means the product's minimum.
 */
export type BuyRequest =
  | { product: 'seats' | 'hours'; quantity: number | null }
  | { product: 'topup'; amountCents: number | null }
  | { product: PackageKey; quantity: 1 };

export type BuyParseError = 'unknownProduct' | 'badQuantity' | 'badAmount';

export type BuyParseResult =
  | { ok: true; request: BuyRequest }
  | { ok: false; error: BuyParseError; product: ProductKey | null };

/** Terms §4.2: a seat subscription's initial term, in months. */
export const SEAT_INITIAL_TERM_MONTHS = 6;

export const BUY_PREFIX = '/buy/';

/** Strict: digits only, no sign, no leading zero, no exponent. */
const POSITIVE_INTEGER = /^[1-9][0-9]{0,8}$/;
/** Euros with up to two decimals, no sign, no exponent, no thousands separator. */
const EURO_AMOUNT = /^(0|[1-9][0-9]{0,8})(\.[0-9]{1,2})?$/;

function isPackageKey(value: string): value is PackageKey {
  return (PACKAGE_KEYS as readonly string[]).includes(value);
}

/** Exact euros-to-cents from the string, never through a float. */
function euroStringToCents(value: string): number | null {
  if (!EURO_AMOUNT.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return cents > 0 && Number.isSafeInteger(cents) ? cents : null;
}

/** The product named by a `/buy/...` pathname, or null. */
export function productFromPath(pathname: string): ProductKey | null {
  if (!pathname.startsWith(BUY_PREFIX)) return null;
  const rest = pathname.slice(BUY_PREFIX.length).replace(/\/$/, '');
  if (rest === 'seats' || rest === 'hours' || rest === 'topup') return rest;
  const match = /^package\/([a-z]+)$/.exec(rest);
  if (match && isPackageKey(match[1])) return match[1];
  return null;
}

/** Read a `/buy/...` location. Syntax only: limits come from the catalogue. */
export function parseBuyLocation(pathname: string, search: string): BuyParseResult {
  const product = productFromPath(pathname);
  if (!product) return { ok: false, error: 'unknownProduct', product: null };
  const params = new URLSearchParams(search);

  if (product === 'seats' || product === 'hours') {
    const raw = params.get('quantity');
    if (raw === null) return { ok: true, request: { product, quantity: null } };
    if (!POSITIVE_INTEGER.test(raw)) return { ok: false, error: 'badQuantity', product };
    return { ok: true, request: { product, quantity: Number(raw) } };
  }

  if (product === 'topup') {
    const raw = params.get('amount');
    if (raw === null) return { ok: true, request: { product, amountCents: null } };
    const cents = euroStringToCents(raw);
    if (cents === null) return { ok: false, error: 'badAmount', product };
    return { ok: true, request: { product, amountCents: cents } };
  }

  return { ok: true, request: { product, quantity: 1 } };
}

export type PricedPurchase = {
  product: CatalogueProduct;
  /** What checkout is asked for. */
  quantity: number;
  /** `unit_amount_cents × quantity`, net of VAT, from the catalogue only. */
  totalCents: number;
};

export type PriceError =
  | { error: 'unknownProduct' }
  | { error: 'belowMinimum'; product: CatalogueProduct }
  | { error: 'aboveMaximum'; product: CatalogueProduct }
  | { error: 'notAMultiple'; product: CatalogueProduct };

export type PriceResult = ({ ok: true } & PricedPurchase) | ({ ok: false } & PriceError);

/**
 * Price a request against the catalogue. The total is the catalogue's unit
 * amount times the quantity; a top-up's amount is turned into a quantity of
 * the catalogue's unit, so it must be a whole number of units.
 */
export function priceRequest(request: BuyRequest, products: CatalogueProduct[]): PriceResult {
  const product = products.find((p) => p.key === request.product);
  if (
    !product ||
    !Number.isSafeInteger(product.unit_amount_cents) ||
    product.unit_amount_cents <= 0
  ) {
    return { ok: false, error: 'unknownProduct' };
  }

  let quantity: number;
  if (request.product === 'topup') {
    if (request.amountCents === null) {
      quantity = product.min_quantity;
    } else {
      if (request.amountCents % product.unit_amount_cents !== 0) {
        return { ok: false, error: 'notAMultiple', product };
      }
      quantity = request.amountCents / product.unit_amount_cents;
    }
  } else {
    quantity = request.quantity ?? product.min_quantity;
  }

  if (quantity < product.min_quantity) return { ok: false, error: 'belowMinimum', product };
  if (quantity > product.max_quantity) return { ok: false, error: 'aboveMaximum', product };

  return { ok: true, product, quantity, totalCents: product.unit_amount_cents * quantity };
}
